import { blogCover } from "@/data/blog-covers";
import { Link } from "@tanstack/react-router";
import { EN_BLOG_POSTS, getEnBlogPost } from "@/data/blog-en";
import { Calendar, Clock, ChevronLeft, ChevronRight, BookOpen, CheckCircle2, MessageCircle, Phone } from "lucide-react";
import { FaqAccordion } from "@/components/seo/Blocks";
import EducationalTripleKeyLinks from "@/components/EducationalTripleKeyLinks";
import ConsultantAdvisoryBlock from "@/components/ConsultantAdvisoryBlock";

const SITE = "https://nyclanguageinstitute.com";

/**
 * Body renderer for /en/blog/$slug — kept out of the route file so the 814 KB
 * EN article corpus stays in this code-split chunk. FAQPage JSON-LD is emitted
 * here (SSR-rendered) since route head() only sees the lightweight index.
 */
export function BlogArticleEn({ slug }: { slug: string }) {
  const p = getEnBlogPost(slug)!;
  const url = `${SITE}/en/blog/${p.slug}`;
  const idx = EN_BLOG_POSTS.findIndex((x) => x.slug === slug);
  const prev = idx > 0 ? EN_BLOG_POSTS[idx - 1] : null;
  const next = idx < EN_BLOG_POSTS.length - 1 ? EN_BLOG_POSTS[idx + 1] : null;
  const related = p.relatedSlugs
    .map((s) => EN_BLOG_POSTS.find((b) => b.slug === s))
    .filter((x): x is (typeof EN_BLOG_POSTS)[number] => !!x);

  return (
    <main className="min-h-screen bg-white">
      {p.faqs.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "@id": `${url}#faq`,
              inLanguage: "en",
              mainEntity: p.faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }),
          }}
        />
      )}

      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/en" className="hover:text-amber-300">Home</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/en/blog" className="hover:text-amber-300">Blog</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{p.category}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-500/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-amber-300">
            <BookOpen className="h-3.5 w-3.5" /> {p.category}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">{p.title}</h1>
          <p className="mt-4 text-base text-white/85 sm:text-lg">{p.excerpt}</p>
          <div className="mt-6 flex flex-wrap items-center gap-4 text-[12px] text-white/70">
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> Published {p.publishedAt}</span>
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> Updated {p.updatedAt}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {p.readMinutes} min read</span>
            <span>By {p.author}</span>
          </div>
          <figure className="mt-8 overflow-hidden rounded-2xl border border-white/10 bg-white/5">
            <img
              src={blogCover(p.category, "en").src}
              alt={blogCover(p.category, "en").altEn}
              width={1200}
              height={630}
              fetchPriority="high"
              decoding="async"
              className="h-auto w-full object-cover"
            />
            <figcaption className="px-4 py-2 text-[11px] text-white/60">
              {blogCover(p.category, "en").altEn}
            </figcaption>
          </figure>
        </div>
      </section>

      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-14">
        <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-5 sm:p-6">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">TL;DR</div>
          <ul className="mt-3 space-y-2">
            {p.tldr.map((t) => (
              <li key={t} className="flex items-start gap-2 text-[14px] text-slate-800">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>

        <article className="prose prose-slate mt-10 max-w-none prose-headings:font-display prose-headings:text-navy-deep prose-h2:text-2xl prose-h2:font-extrabold prose-p:leading-relaxed prose-strong:text-amber-700">
          {p.sections.map((sec) => (
            <section key={sec.h2} className="mt-8 first:mt-0">
              <h2>{sec.h2}</h2>
              {sec.paragraphs.map((para, i) => <p key={i}>{para}</p>)}
              {sec.list && <ul>{sec.list.map((li) => <li key={li}>{li}</li>)}</ul>}
            </section>
          ))}
        </article>

        {p.primaryService && (
          <a href={p.primaryService.path} className="mt-10 flex items-center justify-between gap-4 rounded-2xl border border-amber-300 bg-amber-50/80 p-5 transition hover:-translate-y-0.5 hover:border-amber-500 hover:shadow-md sm:p-6">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">Related service</div>
              <div className="mt-1 font-display text-lg font-extrabold text-navy-deep">{p.primaryService.label}</div>
              <div className="mt-1 text-[12px] text-slate-600">See pricing, timelines &amp; start your order →</div>
            </div>
            <ChevronRight className="h-6 w-6 shrink-0 text-amber-700" />
          </a>
        )}

        <div className="mt-12 rounded-2xl border border-navy-deep/15 bg-gradient-to-br from-navy-deep to-[#152043] p-6 text-white sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-300">Need case-specific advice?</div>
          <h3 className="mt-2 font-display text-xl font-extrabold sm:text-2xl">Free 30-min consultation — reply within 15 minutes</h3>
          <div className="mt-5 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md hover:bg-emerald-400">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <a href="tel:+66832494999" className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md">
              <Phone className="h-4 w-4" /> +66 83-249-4999
            </a>
          </div>
        </div>

        {p.faqs.length > 0 && (
          <section className="mt-12">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep">Frequently Asked Questions</h2>
            <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-2 sm:p-3">
              <FaqAccordion items={p.faqs} />
            </div>
          </section>
        )}

        <div className="mt-10 flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Tags:</span>
          {p.tags.map((t) => (
            <span key={t} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] text-slate-700">#{t}</span>
          ))}
        </div>

        {related.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep">Related articles</h2>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              {related.map((r) => (
                <Link key={r.slug} to="/en/blog/$slug" params={{ slug: r.slug }} className="group block rounded-2xl border border-slate-200 bg-white p-5 transition hover:-translate-y-0.5 hover:border-amber-400 hover:shadow-md">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">{r.category}</div>
                  <div className="mt-1.5 font-display text-base font-extrabold leading-snug text-navy-deep group-hover:text-amber-600">{r.title}</div>
                  <div className="mt-1.5 text-[12px] text-slate-500">{r.readMinutes} min · {r.updatedAt}</div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <nav className="mt-10 flex flex-col gap-3 border-t border-slate-200 pt-8 sm:flex-row sm:justify-between">
          {prev ? (
            <Link to="/en/blog/$slug" params={{ slug: prev.slug }} className="group flex flex-1 items-start gap-3 rounded-xl border border-slate-200 p-4 hover:border-amber-400">
              <ChevronLeft className="mt-0.5 h-5 w-5 text-slate-400 group-hover:text-amber-600" />
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Previous</div>
                <div className="mt-0.5 text-sm font-bold text-navy-deep group-hover:text-amber-600">{prev.title}</div>
              </div>
            </Link>
          ) : <div className="flex-1" />}
          {next ? (
            <Link to="/en/blog/$slug" params={{ slug: next.slug }} className="group flex flex-1 items-start justify-end gap-3 rounded-xl border border-slate-200 p-4 text-right hover:border-amber-400">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Next</div>
                <div className="mt-0.5 text-sm font-bold text-navy-deep group-hover:text-amber-600">{next.title}</div>
              </div>
              <ChevronRight className="mt-0.5 h-5 w-5 text-slate-400 group-hover:text-amber-600" />
            </Link>
          ) : <div className="flex-1" />}
        </nav>

        <ConsultantAdvisoryBlock locale="en" />

        <EducationalTripleKeyLinks mode="en" />
      </div>
    </main>
  );
}

export default BlogArticleEn;
